import React, {useRef, useState} from 'react';
import './TableHeader.scss';
import {t} from 'i18next';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {ReactComponent as ColumnsIcon} from '../../../../assets/images/icon-columns.svg';
import {ReactComponent as ResetIcon} from '../../../../assets/images/icon-reset.svg';
import {ReactComponent as SaveIcon} from '../../../../assets/images/save.svg';
import {
  Box,
  Checkbox,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from '@mui/material';
import {ArrowDropUp} from '@mui/icons-material';
import {ReactComponent as CheckedIcon} from '../../../../assets/images/icon-checked.svg';
import {ReactComponent as UnCheckedIcon} from '../../../../assets/images/icon-unchecked.svg';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../common/arr-menu/ArrMenu';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrTooltip from '../../../common/arr-tooltip/ArrTooltip';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';

export interface SelectSubItem {
  id: string | number;
  name: string;
  disabled?: boolean;
  subItems?: SelectSubItem[];
  checked?: boolean;
}

export interface TableHeaderProps {
  actions?: SelectSubItem[];
  tableColumns?: SelectSubItem[];
  onTableColumnSelectionChange?: (arg0: SelectSubItem) => void;
  onSaveClick?: () => void;
  onResetClick?: () => void;
  onActionSelect?: (action: SelectSubItem | null) => void;
  showResetIcon?: boolean;
  showSaveIcon?: boolean;
  showColumnsIcon?: boolean;
  className?: string;
  hasSelectedRows?: boolean;
  showActionButton?: boolean;
}

const TableHeader: React.FC<TableHeaderProps> = ({
  onActionSelect,
  actions = [],
  tableColumns = [],
  onTableColumnSelectionChange,
  onSaveClick,
  onResetClick,
  showResetIcon = false,
  showSaveIcon = false,
  showActionButton = false,
  showColumnsIcon = false,
  className = '',
  hasSelectedRows = false,
}) => {
  const actionMenuRef = useRef<ArrMenuHandle>(null);
  const columnMenuRef = useRef<ArrMenuHandle>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const columnMenuItemClickHandler = (columnMenuItem: CustomMenuItem) => {
    onTableColumnSelectionChange?.({id: columnMenuItem.id, name: ''});
  };

  const convertToCustomMenuItems = (
    items: SelectSubItem[]
  ): CustomMenuItem[] => {
    return items.map((item) => ({
      id: item.id.toString(),
      label: item.name,
      disabled: item.disabled,
      checked: item.checked,
      submenu: item.subItems
        ? convertToCustomMenuItems(item.subItems)
        : undefined,
    }));
  };

  const handleActionClick = (item: CustomMenuItem) => {
    // Find the original SelectSubItem
    const findOriginalItem = (
      items: SelectSubItem[],
      id: string
    ): SelectSubItem | null => {
      for (const item of items) {
        if (item.id.toString() === id) {
          return item;
        }
        if (item.subItems) {
          const found = findOriginalItem(item.subItems, id);
          if (found) return found;
        }
      }
      return null;
    };

    const originalItem = findOriginalItem(actions, item.id);
    if (originalItem && onActionSelect) {
      onActionSelect(originalItem);
    }
  };

  return (
    <>
      <Box className={`table-header doc-explorer ${className}`}>
        {showActionButton && (
          <Box className="table-header-section">
            <ArrButton
              variant="secondary"
              size="small"
              endIcon={isMenuOpen ? <ArrowDropUp /> : <ArrowDropDownIcon />}
              aria-label={t('docExplorer.header.action')}
              className={`action-dropdown-button ${hasSelectedRows ? 'action-dropdown-button--selected' : ''}`}
              onClick={(e) => {
                if (!hasSelectedRows) return;
                actionMenuRef.current?.open(e.currentTarget);
                setIsMenuOpen(true);
              }}
              dataTestId="table-header-action-button"
            >
              {t('docExplorer.header.action')}
            </ArrButton>
            <ArrMenu
              id="table-header-menu"
              ref={actionMenuRef}
              items={convertToCustomMenuItems(actions)}
              direction="bottom-left"
              onItemClick={handleActionClick}
              className="action-submenu"
              onClose={() => {
                setIsMenuOpen(false);
              }}
            />
          </Box>
        )}
        <Box className="table-header-section table-header-actions">
          <Box
            className="action-buttons-right"
            role="group"
            aria-label="Action buttons"
          >
            {showResetIcon && (
              <ArrTooltip
                content={t('docExplorer.header.reset')}
                trigger="hover"
                placement="top"
                tabIndex={-1}
              >
                <ArrIconButton
                  size="medium"
                  onClick={() => onResetClick?.()}
                  ariaLabel={t('docExplorer.header.reset')}
                  icon={<ResetIcon fontSize="medium" />}
                  shape="default"
                  testId="table-header-reset-icon-button"
                />
              </ArrTooltip>
            )}
            {showSaveIcon && (
              <ArrTooltip
                content={t('docExplorer.header.save')}
                trigger="hover"
                placement="top"
                tabIndex={-1}
              >
                <ArrIconButton
                  size="medium"
                  onClick={() => onSaveClick?.()}
                  ariaLabel={t('docExplorer.header.save')}
                  icon={<SaveIcon fontSize="medium" />}
                  shape="default"
                  testId="table-header-save-icon-button"
                />
              </ArrTooltip>
            )}
            {showColumnsIcon && (
              <ArrTooltip
                content={t('docExplorer.header.columns')}
                trigger="hover"
                placement="top"
                tabIndex={-1}
              >
                <ArrIconButton
                  size="medium"
                  onClick={(e) => {
                    columnMenuRef.current?.open(e.currentTarget);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab' || e.code === 'Space') {
                      e.preventDefault();
                      columnMenuRef.current?.open(e.currentTarget);
                    }
                  }}
                  ariaLabel={t('docExplorer.header.columns')}
                  icon={<ColumnsIcon fontSize="medium" />}
                  shape="default"
                  testId="table-header-columns-icon-button"
                />
              </ArrTooltip>
            )}
          </Box>
        </Box>
      </Box>
      <ArrMenu
        data-testid="doc-cloumn-selection-menu"
        id="doc-cloumn-selection-menu"
        ref={columnMenuRef}
        items={convertToCustomMenuItems(tableColumns)}
        direction="bottom-left"
        className="doc-column-menu"
        keepOpenOnItemClick={true}
        renderItem={(item) => (
          <MenuItem
            data-testid="doc-cloumn-selection-menu-item"
            dense
            disableRipple
            disabled={item.disabled}
            onClick={() => columnMenuItemClickHandler(item)}
            role="menuitemcheckbox"
            aria-checked={item.checked}
            aria-disabled={item.disabled}
          >
            <ListItemIcon>
              <Checkbox
                data-testid={`doc-explorer-table-header-column-checkbox-${item.id}`}
                edge="start"
                checked={item.checked}
                tabIndex={-1}
                disableRipple
                size="small"
                icon={<UnCheckedIcon />}
                checkedIcon={<CheckedIcon />}
                disabled={item.disabled}
              />
            </ListItemIcon>

            <ListItemText primary={item.label} />
          </MenuItem>
        )}
      />
    </>
  );
};

export default TableHeader;
